/* eslint-disable max-lines-per-function */
import type { Maybe } from '@adornis/base/utilTypes.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { BuchungsheldLitElement } from '@adornis/buchungsheld-components/BuchungsheldLitElement.js';
import '@adornis/buchungsheld-components/bh-breadcrumb.js';
import { WorkshopDocument } from '@adornis/buchungsheld-db/entities/WorkshopDocument.js';
import { WorkshopNote } from '@adornis/buchungsheld-db/entities/WorkshopNote.js';
import { Workshop } from '@adornis/buchungsheld-db/entities/workshop.js';
import { dataviewEmptyState } from '@adornis/buchungsheld-db/global-helpers.js';
import '@adornis/buchungsheld-theme/components/tr-button-small.js';
import '@adornis/buchungsheld-theme/fonts/fonts.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import '@adornis/chemistry/elements/components/x-section.js';
import '@adornis/chemistry/elements/components/x-tabs.js';
import type { ColumnDefinition } from '@adornis/data-view/ColumnDefinition.js';
import type { DataViewOptions } from '@adornis/data-view/client/data-view-options.js';
import '@adornis/data-view/client/x-data-view.js';
import { View } from '@adornis/data-view/db/view.js';
import { XDialog } from '@adornis/dialog/x-dialog.js';
import { AdornisFile } from '@adornis/file-utils/db/files.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import '@adornis/forms/x-input.js';
import type { XDropdown } from '@adornis/popover/x-dropdown.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { createRef, ref } from 'lit/directives/ref.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { DateTime } from 'luxon';
import { BehaviorSubject, filter, switchMap, takeUntil } from 'rxjs';
import '../../../../components/bh-dropdown-action-content.js';
import { smallIconButton } from '../../../../components/bh-smol.js';
import { BHPromptWorkshopDocumentModify } from '../../../../prompts/bh-prompt-workshop-document-modify.js';
import { BHPromptWorkshopNoteModify } from '../../../../prompts/bh-prompt-workshop-note-modify.js';

/**
 * @element bh-page-admin-einrichtung-workshops-notes
 */
@customElement('bh-page-admin-einrichtung-workshops-notes')
export class BHPageAdminEinrichtungWorkshopsNotes extends BuchungsheldLitElement {
  @property({ attribute: 'institution-id' }) institutionID?: string;
  @property({ attribute: 'workshop-id' }) workshopID = new RXController<string>(this, null);

  @state() private readonly _workshop = new RXController(
    this,
    this.workshopID.observable.pipe(
      filter(Boolean),
      switchMap(id => getByID(Workshop, id)({ documents: AdornisFile.allFields, name: 1 })),
    ),
  );

  @state() private readonly viewMode = new BehaviorSubject<'notes' | 'documents'>('notes');

  get dataviewOptions() {
    if (this.viewMode.value === 'notes') return this.dataviewOptionsForNotes;
    return this.dataviewOptionsForDocuments;
  }

  get dataviewOptionsForNotes() {
    return {
      class: WorkshopNote,
      emptyState: () => dataviewEmptyState({}),
      selectionSet: { title: 1, createdAt: 1, text: 1, translatedNoteHTML: { text: 1 } },
      view: new View({
        filter: new AdornisFilter({ searchFields: ['workshopID'], search: this.workshopID.value }),
      }),
      customColumns: [
        { fieldName: 'title' },
        {
          customID: 'content',
          columnName: 'Inhalt',
          selectionSet: { translatedNoteHTML: { text: 1 } },
          renderItem: instance => unsafeHTML(instance.translatedNoteHTML[0]?.text ?? '----'),
        },
        {
          fieldName: 'createdAt',
          columnWidth: 'max-content',
          getFieldMapping: instance => instance.createdAt.toLocaleString(DateTime.DATE_MED),
        },
        {
          columnName: '',
          customID: '_options',
          selectionSet: { adminOnly: 1 },
          renderItem: instance => this.renderNoteOptionsColumn(instance),
          columnWidth: 'max-content',
        },
      ] as Array<ColumnDefinition<typeof WorkshopNote>>,
    } satisfies DataViewOptions<typeof WorkshopNote>;
  }

  get dataviewOptionsForDocuments() {
    return {
      class: WorkshopDocument,
      emptyState: () => dataviewEmptyState({}),
      selectionSet: {
        file: AdornisFile.allFields,
        title: 1,
        createdAt: 1,
        fileID: 1,
        text: 1,
        sharedWithParticipantsWithSuccessfullParticipation: 1,
        sharedWithParticipantsWithoutSuccessfullParticipation: 1,
        sharedWithLecturers: 1,
      },
      view: new View({
        filter: new AdornisFilter({ searchFields: ['workshopID'], search: this.workshopID.value }),
      }),
      customColumns: [
        { fieldName: 'title' },
        {
          columnName: 'Datei',
          fieldName: 'file',
          selectionSet: { ...AdornisFile.allFields, fileID: 1, file: AdornisFile.allFields },
          renderItem: instance => this.renderDocumentFileColumn(instance),
        },
        {
          fieldName: 'createdAt',
          columnWidth: 'max-content',
          getFieldMapping: instance => instance.createdAt.toLocaleString(DateTime.DATE_MED),
        },
        {
          columnName: '',
          customID: '_options',
          renderItem: instance => this.renderDocumentOptionsColumn(instance),
          columnWidth: 'max-content',
        },
      ] as Array<ColumnDefinition<typeof WorkshopDocument>>,
    } satisfies DataViewOptions<typeof WorkshopDocument>;
  }

  override connectedCallback() {
    super.connectedCallback();
    this.viewMode.pipe(takeUntil(this.disconnected)).subscribe(_viewMode => {
      this.requestUpdate();
    });
  }

  override render() {
    if (this.workshopID.isLoading || this._workshop.isLoading) {
      return html` <x-loading-state></x-loading-state> `;
    }

    this.exposeAction('addNote', {
      icon: 'add',
      text: this._translation.translate('create'),
      callback: async () => this.add(),
    });

    return html`
      <x-tabs .selected=${this.viewMode.value}>
        <x-tab label="notes" @click=${() => this.viewMode.next('notes')}> Notizen </x-tab>
        <x-tab label="documents" @click=${() => this.viewMode.next('documents')}> Dokumente </x-tab>
      </x-tabs>
      <x-data-view .options=${this.dataviewOptions}></x-data-view>
    `;
  }

  private add() {
    if (this.viewMode.value === 'notes') {
      BHPromptWorkshopNoteModify.add(this.workshopID.value!);
      return;
    }

    if (this.viewMode.value === 'documents') {
      BHPromptWorkshopDocumentModify.add(this.workshopID.value!);
      return;
    }

    throw new Error('unreachable');
  }

  // datview columns ----------------------------------------------
  renderDocumentOptionsColumn(instance: WorkshopDocument) {
    const dropdownReference = createRef<XDropdown>();
    return html`
      <x-flex space="xs" wrap horizontal>
        ${[
          {
            icon: 'edit',
            color: this.colors.secondary.shade(300),
            action: () => BHPromptWorkshopDocumentModify.edit(instance),
            tooltipText: this._translation.translate('edit'),
          },
          {
            icon: 'visibility',
            color: this.colors.secondary.shade(300),
            action: () => goTo(AdornisFile.getServeLink(instance.fileID), { target: '_blank' }),
            tooltipText: this._translation.translate('preview'),
          },
          {
            icon: 'lock',
            color: this.colors.tone.success,
            action: e => {
              e.stopPropagation();
              dropdownReference.value?.open();
              console.log({ ref: dropdownReference.value });
              console.log('ok');
            },
            tooltipText: null,
          },
          {
            icon: 'delete',
            color: this.colors.tone.error,
            action: async () => {
              if (!(await XDialog.confirm(this._translation.translate('settings', 'del_crt_cnf')))) return;
              const fileID = instance.fileID;
              await AdornisFile.removeEntity(AdornisFile._class, fileID)();
              await instance.remove();
            },
            tooltipText: this._translation.translate('delete'),
          },
        ].map(smallIconButton)}

        <x-dropdown ${ref(dropdownReference)}>
          <bh-dropdown-action-content
            slot="dropdown-content"
            heading=${'freigegeben für'}
            .dialogRef=${dropdownReference}
            .actions=${() => [
              {
                icon: this.cbIcon(instance.sharedWithParticipantsWithSuccessfullParticipation),
                text: 'Teilnehmer mit erfolgreicher Teilnahme',
                action: () =>
                  XDialog.waitFor(
                    (() => {
                      instance.sharedWithParticipantsWithSuccessfullParticipation =
                        !instance.sharedWithParticipantsWithSuccessfullParticipation;
                      return instance.save();
                    })(),
                  ),
              },
              {
                icon: this.cbIcon(instance.sharedWithParticipantsWithoutSuccessfullParticipation),
                text: 'Teilnehmer ohne erfolgreiche Teilnahme',
                action: () =>
                  XDialog.waitFor(
                    (() => {
                      instance.sharedWithParticipantsWithoutSuccessfullParticipation =
                        !instance.sharedWithParticipantsWithoutSuccessfullParticipation;
                      return instance.save();
                    })(),
                  ),
              },
              {
                icon: this.cbIcon(instance.sharedWithLecturers),
                text: 'Dozenten',
                action: () =>
                  XDialog.waitFor(
                    (() => {
                      instance.sharedWithLecturers = !instance.sharedWithLecturers;
                      return instance.save();
                    })(),
                  ),
              },
            ]}
          ></bh-dropdown-action-content>
        </x-dropdown>
      </x-flex>
    `;
  }

  renderNoteOptionsColumn(instance: WorkshopNote) {
    return html`
      <x-flex space="xs" wrap horizontal>
        ${[
          {
            icon: 'edit',
            color: this.colors.secondary.shade(300),
            action: () => BHPromptWorkshopNoteModify.edit(instance),
            tooltipText: this._translation.translate('edit'),
          },
          {
            icon: 'lock',
            color: instance.adminOnly ? this.colors.tone.success : this.colors.secondary.shade(300),
            action: () => {
              instance.adminOnly = !instance.adminOnly;
              instance.save();
            },
            tooltipText: instance.adminOnly
              ? this._translation.translate('workshop-notes', 'share')
              : this._translation.translate('workshop-notes', 'unshare'),
          },
          {
            icon: 'delete',
            color: this.colors.tone.error,
            action: async () => {
              if (!(await XDialog.confirm(this._translation.translate('settings', 'del_crt_cnf')))) return;
              await instance.remove();
            },
            tooltipText: this._translation.translate('delete'),
          },
        ].map(smallIconButton)}
      </x-flex>
    `;
  }

  renderDocumentFileColumn(instance: WorkshopDocument) {
    const text = instance.file //
      ? instance.file.meta?.fileName
      : this._translation.translate('settings', 'no_file');
    return html` <x-text> ${text} </x-text> `;
  }

  cbIcon(condition: Maybe<boolean>) {
    return condition ? 'check_box' : 'check_box_outline_blank';
  }
}
